import React, { useEffect, useMemo, useRef, useState } from 'react';

import { IIcon } from '@rbilabs/components-library';
import { Icon } from '@rbilabs/components-library/build/components/icon';
import { Box } from '@rbilabs/components-library/build/components/layout';
import { format, sub } from 'date-fns';
import { isNil } from 'lodash';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router';

import { IStore, IStoreHoursOfOperation } from '@rbi-ctg/store';
import { ActionButton } from 'components/action-button';
import { Content, HeroContainer } from 'components/modal/css';
import { Picture } from 'components/picture';
import { StaticMap } from 'components/static-map';
import { DirectionsLink } from 'components/store-card/directions-link';
import { StoreStatus } from 'components/store-status';
import { OperationalStatus, useGetRestaurantQuery } from 'generated/rbi-graphql';
import { useGetRestaurantsQuery } from 'generated/sanity-graphql';
import { useSelectRestaurant } from 'hooks/store-card';
import { useDistanceWarning } from 'hooks/use-distance-warning';
import { useErrorModal } from 'hooks/use-error-modal';
import { useRestaurantGroups } from 'hooks/use-restaurant-groups';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import {
  LOYALTY_EXCLUDED_GROUP,
  storeHoursSummary,
} from 'pages/store-locator/new-ui/store-card/constants';
import { serviceModeToIntlId } from 'pages/store-locator/new-ui/store-card/hooks/use-service-mode-text';
import { useGeolocation } from 'state/geolocation';
import { useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { ServiceMode } from 'state/service-mode';
import { TLocalizationKey } from 'types/i18n';
import {
  TWeekdayAbbreviationInput,
  convertWeekdayAbbreviationToSanityPattern,
  isDateTimeLessThanOtherDateTime,
} from 'utils/dateTime';
import { convertCountryStringToIsoCountryCode2 } from 'utils/i18n';
import { LaunchDarklyFlag } from 'utils/launchdarkly';
import * as location from 'utils/location';
import {
  mergeRestaurantData,
  readableTimeInterval,
  useIsMobileOrderingAvailable,
} from 'utils/restaurant';
import { routes } from 'utils/routing';
import { getStoreSEOSlug } from 'utils/slugify';

import { StoreMeta } from './meta';
import {
  Address,
  AddressAndPhone,
  Amenity,
  Background,
  ButtonContainer,
  Container,
  DayWeek,
  FeatureIcon,
  HoursOfOperationWrapper,
  HoursTitle,
  Inner,
  InnerContentWrapper,
  LoyaltyTag,
  LoyaltyTagsContainer,
  PhoneNumber,
  PrefixSpan,
  SectionSubtitle,
  SpacingLine,
  StoreHourContainer,
  StoreHourWrapper,
  StoreModal,
  StoreStatusWrapper,
  StyledLoyaltyIcon,
  WaysToOrder,
  WeekDayWrapper,
} from './store-info-modal.styled';

export const serviceModeToIcon: Record<ServiceMode, { iconName: keyof IIcon; iconHeight: string }> =
  {
    [ServiceMode.CURBSIDE]: { iconName: 'curbside', iconHeight: '32px' },
    [ServiceMode.DELIVERY]: { iconName: 'delivery', iconHeight: '32px' },
    [ServiceMode.DRIVE_THRU]: { iconName: 'driveThru', iconHeight: '32px' },
    [ServiceMode.EAT_IN]: { iconName: 'dineIn', iconHeight: '32px' },
    [ServiceMode.TAKEOUT]: { iconName: 'cart', iconHeight: '32px' },
    [ServiceMode.TABLE_SERVICE]: { iconName: 'restaurant', iconHeight: '32px' },
  };

interface IHoursProps {
  hours: IStoreHoursOfOperation;
  additionalHours: IStoreHoursOfOperation;
  title: string;
}

const Hours = ({ title, hours, additionalHours }: IHoursProps) => {
  const { formatMessage } = useIntl();
  const hoursOfOperation = readableTimeInterval(
    formatMessage({ id: 'hoursOfOperationFallbackMessage' }),
    formatMessage({ id: 'open24Hours' }),
    formatMessage({ id: 'restaurantInfoTimeFormat' })
  );
  const enableHighlightBreakingTimeSlot = useFlag(
    LaunchDarklyFlag.ENABLE_HIGHLIGHT_BREAKING_TIME_SLOT
  );

  const dateNow = new Date(Date.now());

  const todayWeekday = format(dateNow, 'iiii').toLowerCase();
  const abbreviatedTodayWeekday = format(dateNow, 'iii').toLowerCase() as TWeekdayAbbreviationInput;
  const yesterdayWeekday = format(sub(dateNow, { days: 1 }), 'iiii').toLowerCase();
  const abbreviatedYesterdayWeekday = format(
    sub(dateNow, { days: 1 }),
    'iii'
  ).toLowerCase() as TWeekdayAbbreviationInput;

  if (!hours) {
    return null;
  }

  return (
    <div>
      <HoursTitle>{title}</HoursTitle>
      {storeHoursSummary.map(([messageId, abbreviatedDay]) => {
        const openingHours = hours[`${abbreviatedDay}Open`];
        const closingHours = hours[`${abbreviatedDay}Close`];

        const additionalOpeningHours = additionalHours
          ? additionalHours[`${abbreviatedDay}Open`]
          : '';
        const additionalClosingHours = additionalHours
          ? additionalHours[`${abbreviatedDay}Close`]
          : '';
        const isAdditionalHoursAvailable =
          enableHighlightBreakingTimeSlot && additionalOpeningHours && additionalClosingHours;

        const todayOpeningHours =
          hours[`${convertWeekdayAbbreviationToSanityPattern(abbreviatedTodayWeekday)}Open`];
        const todayClosingHours =
          hours[`${convertWeekdayAbbreviationToSanityPattern(abbreviatedTodayWeekday)}Close`];

        const yesterdayClosingHours =
          hours[`${convertWeekdayAbbreviationToSanityPattern(abbreviatedYesterdayWeekday)}Close`];
        const yesterdayOpeningHours =
          hours[`${convertWeekdayAbbreviationToSanityPattern(abbreviatedYesterdayWeekday)}Open`];

        const parseStoreHourAsDate = (
          hour: string | undefined,
          yesterday = false
        ): Date | undefined => {
          if (!hour) {
            return;
          }

          let date = new Date(dateNow);

          if (yesterday) {
            date.setDate(date.getDate() - 1); //setting the day of yesterday;
          }

          const year = date.getFullYear();
          const month = date.getMonth() + 1;
          const day = date.getDate();

          const dateTimeString = `${year}/${month}/${day} ${hour}`;

          return new Date(dateTimeString);
        };

        const getWeekday = (): boolean => {
          if (!todayOpeningHours) {
            if (yesterdayClosingHours < yesterdayOpeningHours) {
              return isDateTimeLessThanOtherDateTime(
                dateNow,
                parseStoreHourAsDate(yesterdayClosingHours, false)
              );
            }
            return isDateTimeLessThanOtherDateTime(
              dateNow,
              parseStoreHourAsDate(yesterdayClosingHours, true)
            );
          }

          return (
            isDateTimeLessThanOtherDateTime(dateNow, parseStoreHourAsDate(todayOpeningHours)) &&
            isDateTimeLessThanOtherDateTime(dateNow, parseStoreHourAsDate(yesterdayClosingHours)) &&
            isDateTimeLessThanOtherDateTime(
              parseStoreHourAsDate(yesterdayClosingHours),
              parseStoreHourAsDate(todayOpeningHours)
            )
          );
        };

        const getOpeningHoursReference = () => {
          if (isAdditionalHoursAvailable) {
            const openHours = parseStoreHourAsDate(additionalOpeningHours) ?? dateNow;

            if (dateNow >= openHours) {
              return additionalClosingHours;
            }
          }

          return todayOpeningHours;
        };

        const getClosingHoursReference = () => {
          if (isAdditionalHoursAvailable) {
            const closeHours = parseStoreHourAsDate(additionalClosingHours) ?? dateNow;

            if (dateNow <= closeHours) {
              return additionalOpeningHours;
            }
          }

          return todayClosingHours;
        };

        const weekday = getWeekday() ? yesterdayWeekday : todayWeekday;

        const isWeekDayToday = messageId.toLowerCase() === weekday;

        const openingHoursReference = getOpeningHoursReference();
        const closingHoursReference = getClosingHoursReference();

        return (
          <StoreHourContainer key={messageId} isToday={isWeekDayToday}>
            <StoreHourWrapper>
              <WeekDayWrapper>
                <DayWeek>{formatMessage({ id: messageId })}</DayWeek>
                {isWeekDayToday && (
                  <StoreStatusWrapper>
                    <PrefixSpan role="separator"> &bull; </PrefixSpan>
                    <StoreStatus
                      openingHours={openingHoursReference}
                      closingHours={closingHoursReference}
                      yesterdayClosingHours={yesterdayClosingHours}
                      yesterdayOpeningHours={yesterdayOpeningHours}
                      dayWeek={messageId}
                      dateNow={dateNow}
                    />
                  </StoreStatusWrapper>
                )}
              </WeekDayWrapper>
              <HoursOfOperationWrapper>
                <div>
                  {hoursOfOperation(
                    openingHours,
                    !!additionalOpeningHours && isAdditionalHoursAvailable
                      ? additionalOpeningHours
                      : closingHours
                  )}
                </div>
                {isAdditionalHoursAvailable && (
                  <div>{hoursOfOperation(additionalClosingHours, closingHours)}</div>
                )}
              </HoursOfOperationWrapper>
            </StoreHourWrapper>
          </StoreHourContainer>
        );
      })}
    </div>
  );
};

interface IStoreInfoModalProps {
  disableCanonical?: boolean;
  onDismiss?: VoidFunction;
  eventData: IEventData;
  showOrderCTA?: boolean;
  store: IStore & { available: boolean };
  onCheckingRestaurantFarAway?: (selectRestaurant: VoidFunction) => void;
  isRestaurantSelectionConfirmed?: boolean;
  isLoadingDistanceWarningCheck?: boolean;
  isCheckingRestaurantFarAway?: boolean;
}

const createWaysToOrderList = ({
  availableServiceModes,
  hideMobileOrdering,
  isMobileOrderingAvailable,
}: {
  availableServiceModes: ServiceMode[];
  hideMobileOrdering: boolean;
  isMobileOrderingAvailable: boolean;
}) => {
  const list = availableServiceModes.map(mode => {
    return {
      icon: serviceModeToIcon[mode].iconName,
      height: serviceModeToIcon[mode].iconHeight,
      text: serviceModeToIntlId[mode],
    };
  });

  if (!hideMobileOrdering && isMobileOrderingAvailable) {
    list.push({
      icon: 'mobile',
      height: '32px',
      text: 'mobileOrdering',
    });
  }

  return list;
};

export const StoreInfoModal = ({
  disableCanonical = false,
  eventData,
  onDismiss,
  showOrderCTA = true,
  store,
  onCheckingRestaurantFarAway,
  isRestaurantSelectionConfirmed,
  isLoadingDistanceWarningCheck,
  isCheckingRestaurantFarAway,
}: IStoreInfoModalProps) => {
  const {
    name: storeName,
    number,
    hasLoyalty,
    phoneNumber,
    restaurantImage,
    latitude: lat,
    longitude: lng,
    physicalAddress,
    customerFacingAddress,
  } = store;

  const { current: position } = useRef({ lat, lng });
  const { activeCoordinates } = useGeolocation();
  const { pathname }: { pathname: string } = useLocation();
  const { formatMessage } = useIntl();
  const { fetchingPosData } = useOrderContext();
  const { storeHasMobileLoyalty } = useRestaurantGroups(LOYALTY_EXCLUDED_GROUP);
  const {
    availableServiceModes,
    restaurantCanBeSelected,
    serviceModeStatus,
    allServiceModesUnavailable,
  } = useServiceModeStatus(store);

  const enableOrderingFlag = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const enableAmenitiesSection = useFlag(LaunchDarklyFlag.ENABLE_AMENITIES_SECTION);
  const enableHideOrderHereButton = useFlag(
    LaunchDarklyFlag.ENABLE_HIDE_STORE_MODAL_ORDER_HERE_BUTTON
  );
  const enableShowAddress2BeforeCity = useFlag(LaunchDarklyFlag.ENABLE_SHOW_ADDRESS2_BEFORE_CITY);
  const enableLoyaltyTags = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_TAGS_STORE_LOCATOR);
  const hideMobileOrdering = useFlag(LaunchDarklyFlag.HIDE_MOBILE_ORDERING);
  const enableOrdering = isNil(enableOrderingFlag) || enableOrderingFlag;

  const { city: formattedCity, stateProvince: state, postalCode } = physicalAddress;
  const streetAddress = customerFacingAddress?.locale || physicalAddress?.address1;
  const address2 = customerFacingAddress?.locale ? '' : physicalAddress?.address2;
  const showAddress2 = enableShowAddress2BeforeCity && address2;

  const { selectRestaurant } = useSelectRestaurant({ restaurant: store });

  const shouldShowWaysToOrderSection =
    useIsMobileOrderingAvailable(store) || !allServiceModesUnavailable;

  const canonicalUrl = [`${routes.store}`, getStoreSEOSlug(store)].join('/');
  const isStoreLocatorPage = pathname.includes(routes.store);

  const shouldShowDineIn = serviceModeStatus.EAT_IN.capable && !serviceModeStatus.EAT_IN.disabled;
  const shouldShowTakeout =
    serviceModeStatus.TAKEOUT.capable && !serviceModeStatus.TAKEOUT.disabled;
  const shouldShowDriveThru =
    serviceModeStatus.DRIVE_THRU.capable && !serviceModeStatus.DRIVE_THRU.disabled;
  const shouldShowCurbside =
    serviceModeStatus.CURBSIDE.capable && !serviceModeStatus.CURBSIDE.disabled;
  const shouldShowDelivery = serviceModeStatus.DELIVERY && !serviceModeStatus.DELIVERY.disabled;

  const isMobileOrderingAvailable = useIsMobileOrderingAvailable(store);

  const waysToOrderList = createWaysToOrderList({
    hideMobileOrdering,
    availableServiceModes,
    isMobileOrderingAvailable,
  });

  const storeHoursBreakdown = [
    [shouldShowDineIn || shouldShowTakeout, 'dineInHours', 'diningRoomHours'],
    [shouldShowDriveThru, 'driveThruHours'],
    [shouldShowCurbside, 'curbsideHours'],
    [shouldShowDelivery, 'deliveryHours'],
  ] as const satisfies ReadonlyArray<
    readonly [boolean, TLocalizationKey, string] | readonly [boolean, TLocalizationKey]
  >;

  const orderButtonEnabled = restaurantCanBeSelected && !fetchingPosData;

  const isOrderHereButtonLoading = fetchingPosData || isLoadingDistanceWarningCheck;

  const handleOrderHere = () => {
    if (onCheckingRestaurantFarAway) {
      onCheckingRestaurantFarAway(selectRestaurant);
      return;
    }

    selectRestaurant();
  };

  const amenitiesSection = useMemo(() => {
    if (!store.amenities?.length || !enableAmenitiesSection) {
      return null;
    }

    const amenitiesWithIconSrc = store.amenities.filter(amenity => amenity?.icon?.asset?.url);

    if (!amenitiesWithIconSrc.length) {
      return null;
    }

    return (
      <section data-testid="restaurant-amenities">
        <SectionSubtitle data-testid="in-store-amenities">
          {formatMessage({ id: 'inStoreAmenities' })}
        </SectionSubtitle>
        {amenitiesWithIconSrc.map(amenity => {
          const name = amenity?.name?.locale ?? '';

          return (
            <Amenity key={name}>
              <FeatureIcon src={amenity?.icon?.asset?.url} alt={name} />
              {name}
            </Amenity>
          );
        })}
      </section>
    );
  }, [enableAmenitiesSection, formatMessage, store]);

  useEffect(() => {
    if (isRestaurantSelectionConfirmed) {
      selectRestaurant();
    }

    // We can't use selectRestaurant in the dependencies because it is causing an infinity loop. This function is using useCallback but perhaps is not correctly implemented.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRestaurantSelectionConfirmed]);

  if (isCheckingRestaurantFarAway) {
    return null;
  }

  return (
    <>
      {!disableCanonical && (
        <Helmet data-testid="canonical" link={[{ rel: 'canonical', href: canonicalUrl }]} />
      )}
      <StoreModal onDismiss={onDismiss} data-testid="store-info-modal" eventData={eventData}>
        {isStoreLocatorPage && (
          <StoreMeta
            storeUrl={location.get('href')}
            atCopy={formatMessage({ id: 'at' })}
            streetAddress={streetAddress}
            city={formattedCity}
            state={state}
            postalCode={postalCode}
            phoneNumber={phoneNumber}
            image={restaurantImage}
          />
        )}
        <Container>
          <HeroContainer>
            {restaurantImage ? (
              <Picture image={restaurantImage} alt={storeName} />
            ) : (
              <StaticMap position={position} />
            )}
          </HeroContainer>
          <Content disableFooter={!enableOrdering}>
            <Background>
              <Inner>
                <InnerContentWrapper>
                  <h2 id="modal-heading">{streetAddress.toLowerCase()}</h2>
                  <AddressAndPhone>
                    <DirectionsLink activeCoordinates={activeCoordinates} position={position} />
                    <Address>
                      {showAddress2 && <span data-testid="address2">{address2}, </span>}
                      {`${formattedCity}${state ? ' ' + state : ''}, ${postalCode}`}
                    </Address>
                    {phoneNumber && (
                      <PhoneNumber to={`tel:${phoneNumber}`}>{phoneNumber}</PhoneNumber>
                    )}
                  </AddressAndPhone>
                  {enableLoyaltyTags && (
                    <LoyaltyTagsContainer>
                      {hasLoyalty ? (
                        <LoyaltyTag>
                          <StyledLoyaltyIcon />
                          {formatMessage({ id: 'loyaltyStoreLocatorInStore' })}
                        </LoyaltyTag>
                      ) : null}
                      {storeHasMobileLoyalty(number) ? (
                        <LoyaltyTag>
                          <StyledLoyaltyIcon />
                          {formatMessage({ id: 'loyaltyStoreLocatorMobile' })}
                        </LoyaltyTag>
                      ) : null}
                    </LoyaltyTagsContainer>
                  )}
                  <SpacingLine top={24} bottom={11} />
                  {enableOrdering && (
                    <>
                      {shouldShowWaysToOrderSection && (
                        <SectionSubtitle>{formatMessage({ id: 'waysToOrder' })}</SectionSubtitle>
                      )}
                      <WaysToOrder>
                        {waysToOrderList.map(({ icon, height, text }) => {
                          return (
                            <Amenity key={icon}>
                              <Icon
                                aria-hidden
                                icon={icon}
                                width="40px"
                                height={height}
                                color="icon-default"
                              />
                              {formatMessage({ id: text })}
                            </Amenity>
                          );
                        })}
                      </WaysToOrder>
                      {shouldShowWaysToOrderSection && <SpacingLine top={4} bottom={15} />}
                    </>
                  )}

                  {amenitiesSection}
                </InnerContentWrapper>

                {storeHoursBreakdown.map(
                  ([shouldShow, messageId, hours = messageId]) =>
                    shouldShow && (
                      <Hours
                        key={messageId}
                        title={formatMessage({ id: messageId })}
                        hours={store[hours as Exclude<typeof hours, 'dineInHours'>]}
                        additionalHours={store.additionalHours[hours]}
                      />
                    )
                )}
              </Inner>
            </Background>
          </Content>
          {showOrderCTA && enableOrdering && (
            <ButtonContainer>
              <Box maxWidth="300px" margin="0 auto">
                {!enableHideOrderHereButton && !hideMobileOrdering && (
                  <ActionButton
                    isLoading={isOrderHereButtonLoading}
                    data-testid="store-modal-order-here"
                    fullWidth
                    disabled={!orderButtonEnabled}
                    onClick={handleOrderHere}
                  >
                    {formatMessage({ id: 'orderHere' })}
                  </ActionButton>
                )}
              </Box>
            </ButtonContainer>
          )}
        </Container>
      </StoreModal>
    </>
  );
};

interface IStoreInfoModalContainerProps {
  storeId?: string;
  disableCanonical?: boolean;
  canonicalUrl?: string;
  storeNumber?: string;
  showOrderCTA?: boolean;
}

export const StoreInfoModalContainer = (props: IStoreInfoModalContainerProps) => {
  const [isRestaurantSelectionConfirmed, setIsRestaurantSelectionConfirmed] = useState(false);
  const [isCheckingRestaurantFarAway, setIsCheckingRestaurantFarAway] = useState(false);
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const { id: storeIdFromUrl, storeNumber: storeNumberFromUrl } = useParams();

  const id = props.storeId || storeIdFromUrl;
  const storeNumber = props.storeNumber || storeNumberFromUrl;

  const handleInfoModalDismiss = () => {
    navigate(routes.storeLocator);
  };

  const [ErrorDialog] = useErrorModal({
    init: true,
    modalAppearanceEventMessage: 'Error: Fetching Restaurant Info Failure',
  });
  const [ErrorDialogIsNotOpened] = useErrorModal({
    init: true,
    modalAppearanceEventMessage: 'Error: Restaurant is not OPEN',
    onDismiss: handleInfoModalDismiss,
    onConfirm: handleInfoModalDismiss,
  });
  const getRestaurantFromSanity = useGetRestaurantsQuery({
    skip: !storeNumber && !id,
    variables: {
      filter: {
        _id: { eq: id },
        number: { eq: storeNumber },
      },
      limit: 1,
    },
  });

  const {
    isLocationConfirmationNeeded,
    ConfirmLocationModal,
    dismissDialog,
    openConfirmationModal,
    isLoading: isLoadingDistanceWarningCheck,
    isGeolocationEnabled,
  } = useDistanceWarning({ isMobileOrdering: true });

  const secondQueryVariables = useMemo(
    () => ({
      storeId: storeNumber || getRestaurantFromSanity?.data?.allRestaurant[0].number,
    }),
    [storeNumber, getRestaurantFromSanity]
  );
  const getRestaurantFromRBI = useGetRestaurantQuery({
    fetchPolicy: 'cache-and-network',
    skip: !secondQueryVariables.storeId,
    variables: secondQueryVariables,
  });

  const { loading, restaurant, error } = useMemo(
    () => ({
      loading: getRestaurantFromRBI.loading || getRestaurantFromSanity.loading,
      restaurant:
        getRestaurantFromRBI.data &&
        getRestaurantFromSanity.data &&
        getRestaurantFromSanity.data.allRestaurant.length !== 0
          ? mergeRestaurantData({
              rbiRestaurant: getRestaurantFromRBI.data.restaurant,
              sanityStore: getRestaurantFromSanity.data.allRestaurant[0] as any,
            })
          : undefined,
      error: getRestaurantFromRBI.error || getRestaurantFromSanity.error || undefined,
    }),
    [getRestaurantFromRBI, getRestaurantFromSanity]
  );

  const handleCheckingRestaurantFarAway = async (selectRestaurant: VoidFunction) => {
    const { latitude: lat, longitude: lng } = restaurant || {};

    if (!lat || !lng) {
      selectRestaurant();
      return;
    }

    const isLocationFarAway = await isLocationConfirmationNeeded({
      lat,
      lng,
    });

    if (isLocationFarAway) {
      setIsCheckingRestaurantFarAway(true);
      openConfirmationModal();
      return;
    }

    selectRestaurant();
  };

  const handleConfirmRestaurantSelection = () => {
    setIsRestaurantSelectionConfirmed(true);
  };

  const handleCancelRestaurantSelection = () => {
    dismissDialog();
    navigate(routes.storeLocator);
  };

  // Should show loading state
  if (loading) {
    return null;
  }

  if (error || !restaurant) {
    return <ErrorDialog />;
  }

  const { physicalAddress } = restaurant;

  if (restaurant.status.toUpperCase() !== OperationalStatus.OPEN) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="none" />
        </Helmet>

        <ErrorDialogIsNotOpened
          heading={formatMessage({ id: 'storeNotFound' })}
          body={formatMessage({ id: 'storeNotExistOrNotAvailable' })}
        />
      </>
    );
  }

  return (
    <>
      <StoreInfoModal
        {...props}
        onDismiss={handleInfoModalDismiss}
        eventData={{ modalAppearanceEventMessage: 'Store Details' }}
        store={restaurant}
        onCheckingRestaurantFarAway={handleCheckingRestaurantFarAway}
        isRestaurantSelectionConfirmed={isRestaurantSelectionConfirmed}
        isLoadingDistanceWarningCheck={isLoadingDistanceWarningCheck}
        isCheckingRestaurantFarAway={isCheckingRestaurantFarAway}
      />
      <ConfirmLocationModal
        streetName={physicalAddress.address1}
        city={`${physicalAddress.city}, ${convertCountryStringToIsoCountryCode2(
          physicalAddress.country || ''
        )}`}
        isGeolocationEnabled={isGeolocationEnabled}
        onConfirm={handleConfirmRestaurantSelection}
        onDismiss={handleCancelRestaurantSelection}
        isOnConfirmLoading={isRestaurantSelectionConfirmed}
      />
    </>
  );
};
